import { ReactNode, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, ShieldAlert, LogIn, Repeat } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { EmailNoConfirmado } from './EmailNoConfirmado';
import { PerfilNoDisponible } from './PerfilNoDisponible';
import { CURRENT_PORTAL_SUBDOMAIN } from '@/lib/portalUrls';
import { CambiarRolDialog } from './CambiarRolDialog';
import { useRolesAsignados } from '@/hooks/useCambiarRolActivo';
import { useRolesAdicionales } from '@/hooks/useRolesAdicionales';

interface ProtectedRouteProps {
  children: ReactNode;
}

// Prospecto (43): registro público de la app de clientes; en el admin solo ve
// portal-cliente, igual que el Cliente.
const BLOCKED_ROLE_NAMES = ['Cliente', 'Directores', 'Prospecto'];

const CHANGE_PASSWORD_PATH = '/auth/change-password';

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, isLoading, isProfileLoading, mustChangePassword, profile, refreshProfile } = useAuth();
  const location = useLocation();
  // Multi-rol: quien quedó con un rol bloqueado (p. ej. Cliente activo tras
  // entrar por clientes.sozu.com) pero tiene otro rol de admin, puede cambiarse
  // desde la pantalla de bloqueo sin volver a iniciar sesión.
  const { elegiblesAdmin } = useRolesAsignados();
  const [isCambiarRolOpen, setIsCambiarRolOpen] = useState(false);
  const { hasSocioRole } = useRolesAdicionales();

  // Allow Cliente role to access portal-cliente routes
  const isPortalClienteRoute = location.pathname.startsWith('/admin/portal-cliente');
  // En socios.sozu.com el socio (fideicomisario) suele tener rol principal
  // Cliente con el 36 solo en user_roles: aquí no se le bloquea el árbol del
  // portal socio y PermissionRoute decide (niega a quien no es socio). Fuera de
  // ese host el Cliente sigue limitado a portal-cliente.
  const isSociosHostRoute =
    CURRENT_PORTAL_SUBDOMAIN === 'socios' &&
    (location.pathname === '/admin' ||
      location.pathname === '/admin/' ||
      location.pathname.startsWith('/admin/portal-socio') ||
      location.pathname === '/admin/access-denied');
  // El portal socio también vive dentro del admin (admin.sozu.com/admin/portal-socio):
  // el socio con rol activo Cliente entra ahí desde cualquier host. PermissionRoute
  // sigue decidiendo con su gate (rol 36 principal o adicional).
  const isPortalSocioRoute =
    location.pathname.startsWith('/admin/portal-socio') && hasSocioRole === true;
  // OJO: hoy esto es siempre false. App.tsx registra /auth/change-password como
  // ruta hermana de /admin, FUERA de este componente, así que ProtectedRoute
  // nunca se monta en esa URL. Se conserva como red de seguridad por si algún
  // día la ruta se anida aquí dentro (evitaría un bucle de redirects contra el
  // guard de mustChangePassword). Los guards reales de esa pantalla —cuenta
  // desactivada y correo sin confirmar— viven en src/pages/auth/ChangePassword.tsx.
  const isChangePasswordRoute = location.pathname === CHANGE_PASSWORD_PATH;

  const handleGoToLogin = () => {
    supabase.auth.signOut({ scope: "local" }).finally(() => {
      window.location.href = '/auth/login';
    });
  };

  // `isProfileLoading` solo cuenta cuando todavía no hay perfil: así el fetch en
  // vuelo tras un SIGNED_IN no cae en la pantalla de error, y un refresco en
  // segundo plano (cambio de rol, realtime) no tapa la app con el spinner.
  if (isLoading || (user && !profile && isProfileLoading)) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Cargando...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }

  // Fail-closed: con sesión pero sin perfil (RPC caída, timeout, o sin fila en
  // usuarios) no sabemos si la cuenta está activa ni qué rol tiene. Antes esto
  // dejaba pasar al contenido protegido.
  if (!profile) {
    return <PerfilNoDisponible onReintentar={refreshProfile} onCerrarSesion={handleGoToLogin} />;
  }

  // Va ANTES del redirect de mustChangePassword: un usuario dado de baja con
  // contraseña temporal se colaba a /auth/change-password y la cambiaba.
  if (profile.activo === false) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center p-8 bg-card rounded-lg shadow-lg max-w-md space-y-4">
          <h1 className="text-2xl font-bold text-destructive">Cuenta Desactivada</h1>
          <p className="text-muted-foreground">
            Tu cuenta ha sido desactivada. Contacta al administrador para más información.
          </p>
          {elegiblesAdmin.length > 0 && (
            <Button className="mr-2" onClick={() => setIsCambiarRolOpen(true)}>
              <Repeat className="mr-2 h-4 w-4" />
              Cambiar de rol
            </Button>
          )}
          <Button variant="destructive" onClick={handleGoToLogin}>
            <LogIn className="mr-2 h-4 w-4" />
            Iniciar Sesión
          </Button>
          {elegiblesAdmin.length > 0 && (
            <CambiarRolDialog open={isCambiarRolOpen} onOpenChange={setIsCambiarRolOpen} />
          )}
        </div>
      </div>
    );
  }

  // Roles de portal/externos (roles.requiere_confirmacion_email) no entran hasta
  // verificar su correo. La excepción de /auth/change-password no protege nada
  // desde aquí (ver nota arriba: esa ruta no pasa por ProtectedRoute); el mismo
  // gate, con la tolerancia al flujo de confirmación recién completado, está
  // reimplementado en ChangePassword.tsx.
  if (
    profile.requiere_confirmacion_email &&
    !profile.email_confirmado &&
    !isChangePasswordRoute
  ) {
    return (
      <EmailNoConfirmado
        email={profile.email}
        onActualizarEstado={refreshProfile}
        onCerrarSesion={handleGoToLogin}
      />
    );
  }

  if (mustChangePassword && !isChangePasswordRoute) {
    return <Navigate to={CHANGE_PASSWORD_PATH} replace />;
  }

  // Mientras se consulta user_roles, no pintar "Acceso No Autorizado" a un socio
  // que entra al portal socio con rol activo Cliente.
  if (
    hasSocioRole === null &&
    profile.rol_nombre === 'Cliente' &&
    location.pathname.startsWith('/admin/portal-socio')
  ) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (
    BLOCKED_ROLE_NAMES.includes(profile.rol_nombre) &&
    !isPortalClienteRoute &&
    !((isSociosHostRoute || isPortalSocioRoute) && profile.rol_nombre === 'Cliente')
  ) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center p-8 bg-card rounded-lg shadow-lg max-w-md space-y-4">
          <ShieldAlert className="h-16 w-16 text-destructive mx-auto" />
          <h1 className="text-2xl font-bold text-destructive">
            Acceso No Autorizado
          </h1>
          <p className="text-muted-foreground">
            Tu tipo de usuario no tiene acceso a este sistema.
            Contacta al administrador si crees que esto es un error.
          </p>
          <Button variant="destructive" onClick={handleGoToLogin}>
            <LogIn className="mr-2 h-4 w-4" />
            Iniciar Sesión
          </Button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
